import { withDependencies, named } from '@wix/thunderbolt-ioc'
import {
	SiteFeatureConfigSymbol,
	PageFeatureConfigSymbol,
	ExperimentsSymbol,
	LoggerSymbol,
} from '@wix/thunderbolt-symbols'
import type { Experiments, ModuleLoader, ILogger, ISessionManager } from '@wix/thunderbolt-symbols'
import { ESMLoaderSymbol } from 'feature-builder-module-loader'
import { SessionManagerSymbol } from 'feature-session-manager'

import type {
	BuilderContextProvidersSiteConfig,
	BuilderContextProvidersPageConfig,
	ContextProviderLoader,
	ContextProviderRegistry,
} from './types'
import { ContextProviderRegistrySymbol, name } from './symbols'

export const BuilderContextProvidersLoader = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		named(PageFeatureConfigSymbol, name),
		ESMLoaderSymbol,
		ContextProviderRegistrySymbol,
		LoggerSymbol,
		ExperimentsSymbol,
		SessionManagerSymbol,
	] as const,
	(
		siteFeatureConfig: BuilderContextProvidersSiteConfig,
		pageFeatureConfig: BuilderContextProvidersPageConfig,
		moduleLoader: ModuleLoader,
		contextProviderRegistry: ContextProviderRegistry,
		logger: ILogger,
		experiments: Experiments,
		sessionManager: ISessionManager
	): ContextProviderLoader => {
		return {
			retrieveContextProviders: async (compId: string) => {
				if (!experiments['specs.thunderbolt.contextProviders']) {
					return
				}

				const contexts = pageFeatureConfig.compIdToContextsMap[compId] || []
				await Promise.all(
					contexts.map(async (context) => {
						const { contextType } = context
						try {
							const contextProviderData = siteFeatureConfig.contextProviders[contextType]
							if (!contextProviderData) {
								throw new Error(`Context type "${contextType}" not found in site config`)
							}
							const urlToLoad =
								process.env.PACKAGE_NAME === 'thunderbolt-ds'
									? contextProviderData.editorComponentUrl || contextProviderData.componentUrl
									: contextProviderData.componentUrl

							const contextProviderModule: any = await moduleLoader.loadModule(urlToLoad)

							if (typeof contextProviderModule.injectAccessTokenGetter === 'function') {
								const tokenGetter = sessionManager.getBoundAccessTokenFunction(contextProviderData.appDefinitionId)
								contextProviderModule.injectAccessTokenGetter(tokenGetter)
							}

							if (!contextProviderModule.default) {
								throw new Error(`Module for "${contextType}" is missing default export (Provider component)`)
							}
							contextProviderRegistry.registerContextProvider(contextType, contextProviderModule)
						} catch (error) {
							logger.captureError(error, {
								tags: { feature: 'builder-context-providers', phase: 'loading' },
								extra: { contextType, compId },
							})
							throw error
						}
					})
				)
			},
		}
	}
)
